import { SparksAIIcon } from "@cpj-ai/panel-npm-package";
import React, { useEffect, useState } from "react";

interface Stage {
  id: number;
  title: string;
  description: string;
  durationSeconds: number;
}

interface AiAnalysisProgressProps {
  onComplete?: () => void;
  isCompleted?: boolean;
  source?: "audio" | "file";
}

const AiAnalysisProgress: React.FC<AiAnalysisProgressProps> = ({
  onComplete,
  isCompleted = false,
  source = "audio",
}) => {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stageProgress, setStageProgress] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);

  const audioStages: Stage[] = [
    {
      id: 1,
      title: "Transcribing Audio",
      description: "Converting your voice recording into text for analysis",
      durationSeconds: 10,
    },
    {
      id: 2,
      title: "Analyzing Content",
      description: "Identifying key job requirements and qualifications",
      durationSeconds: 12,
    },
    {
      id: 3,
      title: "Extracting Details",
      description: "Determining job responsibilities and skills needed",
      durationSeconds: 14,
    },
    {
      id: 4,
      title: "Formatting Description",
      description: "Creating a professional and compelling job description",
      durationSeconds: 16,
    },
    {
      id: 5,
      title: "Final Review",
      description: "Ensuring all details are accurate and complete",
      durationSeconds: 18,
    },
  ];

  const fileStages: Stage[] = [
    {
      id: 1,
      title: "Processing File",
      description: "Extracting content from your uploaded file",
      durationSeconds: 10,
    },
    {
      id: 2,
      title: "Analyzing Content",
      description: "Identifying key job requirements from your file",
      durationSeconds: 12,
    },
    {
      id: 3,
      title: "Extracting Details",
      description: "Determining job responsibilities and required qualifications",
      durationSeconds: 14,
    },
    {
      id: 4,
      title: "Formatting Description",
      description: "Creating a structured job posting from your file",
      durationSeconds: 16,
    },
    {
      id: 5,
      title: "Final Review",
      description: "Ensuring all details are accurate and complete",
      durationSeconds: 18,
    },
  ];

  const stages = source === "audio" ? audioStages : fileStages;
  const totalDuration = stages.reduce((total, stage) => total + stage.durationSeconds, 0);

  useEffect(() => {
    if (isCompleted) {
      setCurrentStageIndex(stages.length - 1);
      setProgress(100);
      setStageProgress(100);
      setIsFinishing(true);
      return;
    }

    let stageTimer: NodeJS.Timeout;

    if (currentStageIndex < stages.length) {
      const currentStage = stages[currentStageIndex];
      const stageDuration = (currentStage?.durationSeconds ?? 0) * 1000 || 0;
      const stageInterval = 50;

      stageTimer = setInterval(() => {
        setStageProgress((prev) => {
          const newProgress = prev + (stageInterval / stageDuration) * 100;

          if (newProgress >= 100) {
            if (currentStageIndex < stages.length - 1) {
              setTimeout(() => {
                setCurrentStageIndex((prevIndex) => prevIndex + 1);
                setStageProgress(0);
              }, 0);
            } else if (!isFinishing) {
              setIsFinishing(true);
            }
            return 100;
          }

          return newProgress;
        });
      }, stageInterval);
    }

    const progressInterval = setInterval(() => {
      const elapsedDuration = stages
        .slice(0, currentStageIndex)
        .reduce((total, stage) => total + stage.durationSeconds, 0);

      const currentStageDuration = stages[currentStageIndex]?.durationSeconds || 0;
      const currentStageElapsed = (currentStageDuration * stageProgress) / 100;

      const totalElapsed = elapsedDuration + currentStageElapsed;
      const overallProgress = (totalElapsed / totalDuration) * 100;

      setProgress(Math.min(overallProgress, 100));
    }, 50);

    return () => {
      clearInterval(stageTimer);
      clearInterval(progressInterval);
    };
  }, [currentStageIndex, isCompleted, isFinishing, onComplete, stages, totalDuration]);

  const currentStage = stages[currentStageIndex];

  const getFinalMessage = () => {
    if (source === "audio") {
      return "Everything looks great! We're preparing your job post based on your recording. Please wait a moment while we finalize the details.";
    } else {
      return "Everything looks great! We're preparing your job post based on your file. Please wait a moment while we finalize the details.";
    }
  };

  return (
    <div className="mt-4 md:mt-6 p-4 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
      <div className="flex items-center mb-3 md:mb-4">
        <div className="relative mr-3 md:mr-4 flex-shrink-0">
          <div className="w-8 h-8 md:w-10 md:h-10 border-t-2 border-r-2 border-blue-500 border-solid rounded-full animate-spin"></div>
          <SparksAIIcon
            viewBox="0 0 24 24"
            className="w-4 h-4 md:w-5 md:h-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 stroke-blue-600"
          />
        </div>
        <div className="flex-grow">
          <h3 className="text-lg md:text-xl font-semibold text-blue-800">
            {isFinishing ? "Success! Finalizing..." : "AI Analysis in Progress"}
          </h3>
          <div className="w-full bg-blue-100 h-2 mt-2 rounded-full overflow-hidden">
            <div
              className="bg-blue-500 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="pl-2 md:pl-14">
        <p className="text-sm md:text-base text-blue-700 mb-3 font-medium leading-relaxed">
          {isFinishing
            ? getFinalMessage()
            : `Stage ${currentStage?.id}/${stages.length}: ${currentStage?.title} - ${currentStage?.description}`}
        </p>

        <div className="bg-white/70 p-3 md:p-4 rounded-lg border border-blue-100 mb-3 md:mb-4">
          {stages.map((stage, index) => (
            <div
              key={stage.id}
              className={`flex items-start ${index !== 0 ? "mt-2 md:mt-3" : ""} ${
                index > currentStageIndex ? "opacity-40" : "opacity-100"
              }`}
            >
              <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 md:mr-3 mt-0.5 flex-shrink-0">
                {index < currentStageIndex ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 md:h-4 md:w-4 text-blue-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : index === currentStageIndex ? (
                  <div className="h-1.5 w-1.5 md:h-2 md:w-2 bg-blue-600 rounded-full animate-pulse"></div>
                ) : (
                  <div className="h-1.5 w-1.5 md:h-2 md:w-2 bg-blue-300 rounded-full"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-xs md:text-sm font-medium ${index <= currentStageIndex ? "text-blue-700" : "text-blue-400"}`}
                >
                  {stage.title}
                </p>
                {index === currentStageIndex && (
                  <div className="w-full bg-blue-50 h-1 mt-1 rounded-full overflow-hidden">
                    <div
                      className="bg-blue-400 h-full rounded-full transition-all duration-200 ease-out"
                      style={{ width: `${stageProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs md:text-sm text-blue-600 leading-relaxed">
          This process typically takes 1-3 minutes depending on the complexity of your{" "}
          {source === "audio" ? "recording" : "file"}. Please don't refresh the page.
        </p>
      </div>
    </div>
  );
};

export default AiAnalysisProgress;
